// @ts-nocheck
import React, { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Search, Edit, Trash2, UserPlus, Download } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import EditSubscriberModal from "./EditSubscriberModal";

interface SubscribersTableProps {
  roleSlug: string;
}

interface UserWithSubscription {
  id: string;
  email: string;
  name: string | null;
  role: string;
  user_id: string;
  // Subscription fields
  subscriber_id?: string | null;
  is_subscribed: boolean;
  source: string;
  subscription_created_at?: string | null;
}

const SubscribersTable: React.FC<SubscribersTableProps> = ({ roleSlug }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [editingUser, setEditingUser] = useState<UserWithSubscription | null>(null);
  
  const queryClient = useQueryClient();

  // Map role slugs to profile role values
  const getRoleFilter = (roleSlug: string) => {
    switch (roleSlug) {
      case 'athlete':
        return 'athlete';
      case 'parent':
        return 'parent';
      case 'college_coach':
        return 'college_coach';
      case 'club_coach':
        return 'club_coach';
      case 'press':
        return 'press';
      case 'vendor':
        return 'vendor';
      case 'admin':
        return 'admin';
      default:
        return roleSlug;
    }
  };

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users-with-subscriptions', roleSlug, searchTerm, currentPage, pageSize],
    queryFn: async () => {
      const roleFilter = getRoleFilter(roleSlug);
      
      // First get the count for pagination
      let countQuery = supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', roleFilter);

      if (searchTerm) {
        countQuery = countQuery.or(`email.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%`);
      }

      const { count, error: countError } = await countQuery;
      if (countError) throw countError;

      // Then get the actual data with pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      let dataQuery = supabase
        .from('profiles')
        .select(`
          id,
          user_id,
          email,
          name,
          role,
          created_at
        `)
        .eq('role', roleFilter)
        .range(from, to)
        .order('created_at', { ascending: false });

      if (searchTerm) {
        dataQuery = dataQuery.or(`email.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%`);
      }

      const { data: profiles, error: profilesError } = await dataQuery;
      if (profilesError) throw profilesError;

      if (!profiles || profiles.length === 0) {
        return { data: [], count: count || 0 };
      }

      // Get subscription data for these users
      const emails = profiles.map(p => p.email).filter(Boolean);
      const { data: subscribers, error: subscribersError } = await (supabase as any)
        .from('communication_subscribers')
        .select('*')
        .in('email', emails);

      if (subscribersError) throw subscribersError;

      // Create a map of email to subscription data
      const subscriptionMap = new Map();
      (subscribers || []).forEach(sub => {
        subscriptionMap.set(sub.email, sub);
      });

      // Transform the data to include subscription information
      const transformedData: UserWithSubscription[] = profiles.map(profile => {
        const subscription = subscriptionMap.get(profile.email);
        
        return {
          id: profile.id,
          email: profile.email,
          name: profile.name,
          role: profile.role,
          user_id: profile.user_id,
          subscriber_id: subscription?.id || null,
          is_subscribed: subscription?.is_subscribed || true,
          source: subscription?.source || 'profile',
          subscription_created_at: subscription?.created_at || profile.created_at,
        };
      });

      return { data: transformedData, count: count || 0 };
    },
  });

  const updateSubscriptionMutation = useMutation({
    mutationFn: async ({ userId, email, isSubscribed, name }: { 
      userId: string; 
      email: string; 
      isSubscribed: boolean; 
      name?: string;
    }) => {
      if (isSubscribed) {
        // Create or update subscription
        const { error } = await (supabase as any)
          .from('communication_subscribers')
          .upsert({
            email: email,
            first_name: name ? name.split(' ')[0] : null,
            last_name: name ? name.split(' ').slice(1).join(' ') || null : null,
            role_slug: roleSlug,
            is_subscribed: true,
            source: 'admin'
          }, {
            onConflict: 'email'
          });
        if (error) throw error;
      } else {
        // Update existing subscription to unsubscribed
        const { error } = await (supabase as any)
          .from('communication_subscribers')
          .update({ is_subscribed: false })
          .eq('email', email);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-with-subscriptions'] });
      toast.success('Subscription updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update subscription');
      console.error('Update error:', error);
    },
  });

  const deleteSubscriptionMutation = useMutation({
    mutationFn: async (email: string) => {
      const { error } = await (supabase as any)
        .from('communication_subscribers')
        .delete()
        .eq('email', email);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-with-subscriptions'] });
      toast.success('Subscription deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete subscription');
      console.error('Delete error:', error);
    },
  });

  const downloadCSV = () => {
    if (!usersData?.data || usersData.data.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Define CSV headers
    const headers = ['name', 'email', 'role', 'source', 'subscribed', 'created_at'];
    
    // Convert data to CSV format
    const csvData = usersData.data.map(user => [
      user.name || '',
      user.email,
      user.role,
      user.source,
      user.is_subscribed ? 'Yes' : 'No',
      user.subscription_created_at ? new Date(user.subscription_created_at).toLocaleDateString() : ''
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'email-subscribers.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    toast.success('CSV file downloaded successfully');
  };

  const toggleSubscription = (user: UserWithSubscription) => {
    updateSubscriptionMutation.mutate({
      userId: user.user_id,
      email: user.email,
      isSubscribed: !user.is_subscribed,
      name: user.name || undefined,
    });
  };

  const handleBulkSubscribe = () => {
    const selectedUsersData = usersData?.data?.filter(user => selectedUsers.includes(user.id)) || [];
    selectedUsersData.forEach(user => {
      updateSubscriptionMutation.mutate({
        userId: user.user_id,
        email: user.email,
        isSubscribed: true,
        name: user.name || undefined,
      });
    });
    setSelectedUsers([]);
  };

  const handleBulkUnsubscribe = () => {
    const selectedUsersData = usersData?.data?.filter(user => selectedUsers.includes(user.id)) || [];
    selectedUsersData.forEach(user => {
      updateSubscriptionMutation.mutate({
        userId: user.user_id,
        email: user.email,
        isSubscribed: false,
        name: user.name || undefined,
      });
    });
    setSelectedUsers([]);
  };

  const handleBulkDelete = () => {
    const selectedUsersData = usersData?.data?.filter(user => selectedUsers.includes(user.id)) || [];
    selectedUsersData.forEach(user => {
      if (user.subscriber_id) {
        deleteSubscriptionMutation.mutate(user.email);
      }
    });
    setSelectedUsers([]);
  };

  const totalPages = Math.ceil((usersData?.count || 0) / pageSize);

  const getSourceBadgeColor = (source: string) => {
    switch (source) {
      case 'signup':
        return 'bg-green-100 text-green-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'import':
        return 'bg-purple-100 text-purple-800';
      case 'profile':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search by email, name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadCSV}
            disabled={!usersData?.data || usersData.data.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            Download CSV
          </Button>
          
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="border rounded px-3 py-1"
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
          
          {selectedUsers.length > 0 && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleBulkSubscribe}>
                <UserPlus className="h-4 w-4 mr-1" />
                Subscribe ({selectedUsers.length})
              </Button>
              <Button variant="outline" size="sm" onClick={handleBulkUnsubscribe}>
                Unsubscribe ({selectedUsers.length})
              </Button>
              <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                Delete ({selectedUsers.length})
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <input
                  type="checkbox"
                  checked={selectedUsers.length === usersData?.data?.length && usersData?.data?.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedUsers(usersData?.data?.map(u => u.id) || []);
                    } else {
                      setSelectedUsers([]);
                    }
                  }}
                />
              </TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Subscribed</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Subscription Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4">
                  Loading users...
                </TableCell>
              </TableRow>
            ) : usersData?.data?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4">
                  No users found for this role
                </TableCell>
              </TableRow>
            ) : (
              usersData?.data?.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers([...selectedUsers, user.id]);
                        } else {
                          setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{user.email}</TableCell>
                  <TableCell>{user.name || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{user.role}</Badge>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={user.is_subscribed}
                      onCheckedChange={() => toggleSubscription(user)}
                    />
                  </TableCell>
                  <TableCell>
                    <Badge className={getSourceBadgeColor(user.source)}>
                      {user.source}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.subscription_created_at ? 
                      new Date(user.subscription_created_at).toLocaleDateString() : 
                      '-'
                    }
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingUser(user)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {user.subscriber_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteSubscriptionMutation.mutate(user.email)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <PaginationItem key={page}>
                  <PaginationLink
                    onClick={() => setCurrentPage(page)}
                    isActive={currentPage === page}
                    className="cursor-pointer"
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            
            <PaginationItem>
              <PaginationNext
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {editingUser && (
        <EditSubscriberModal
          subscriber={editingUser}
          open={!!editingUser}
          onClose={() => setEditingUser(null)}
          onSave={(updates) => {
            updateSubscriptionMutation.mutate({
              userId: editingUser.user_id,
              email: updates.email || editingUser.email,
              isSubscribed: updates.is_subscribed ?? editingUser.is_subscribed,
              name: updates.name || editingUser.name || undefined,
            });
            setEditingUser(null);
          }}
        />
      )}
    </div>
  );
};

export default SubscribersTable;

